/**
 * The trail behind an agreed price (spec §1.2).
 *
 * The backend writes a row whenever a price actually moves: which field, what
 * it was, what it became, who did it and why. The reason is only filled in
 * when the change came through the price POST — a sheet import or an admin
 * edit leaves it blank, and a blank reason is shown as blank rather than
 * dressed up, because "no reason given" is the finding.
 *
 * Pure. The caller passes the shortlist row.
 */

export const PRICE_FIELD_LABELS = {
  influencer_final_price: 'Creator payout',
  final_price: 'Client billing',
};

const isBlank = (value) =>
  value === null || value === undefined || String(value).trim() === '';

/** A finite number, or null. Blank is null rather than zero: unset is not nil. */
function money(value) {
  if (isBlank(value)) return null;
  const parsed = typeof value === 'number' ? value : parseFloat(String(value).trim());
  return Number.isFinite(parsed) ? parsed : null;
}

/** Whether a row records an actual movement. */
function moved(row) {
  const before = money(row.old_value);
  const after = money(row.new_value);
  // Both unreadable means there is nothing to show; otherwise a difference,
  // including the first time a price is set, is a change worth keeping.
  if (before === null && after === null) return false;
  return before !== after;
}

/**
 * The price changes on this line, newest first.
 *
 * Sorted on `changed_at` and stable within a timestamp, so two changes saved
 * together keep the order the server listed them in.
 */
export function historyOf(row) {
  const list = ((row || {}).price_history);
  if (!Array.isArray(list)) return [];

  return list
    .filter((entry) => entry && !isBlank(entry.field) && moved(entry))
    .map((entry, index) => ({ entry, index }))
    .sort((a, b) => {
      const left = String(a.entry.changed_at ?? '');
      const right = String(b.entry.changed_at ?? '');
      return left === right ? a.index - b.index : right.localeCompare(left);
    })
    .map(({ entry }) => entry);
}

/** What to call the field that moved. */
export function fieldLabel(field) {
  if (isBlank(field)) return 'Price';
  if (PRICE_FIELD_LABELS[field]) return PRICE_FIELD_LABELS[field];

  const words = String(field).replace(/_/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}
